import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import * as Sentry from "@sentry/react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { FormValue, Status } from "../../../redux/interfaces/folder.types";
import {
  onLoadAddressOptions,
  onSetAddressFields,
  onUnsetAddressFields,
  onSetGenderFields,
  onSetCountryFields,
} from "../../../redux/actions/requirementsActions";
import { setActivePanels } from "../../../redux/slices/vaultSlice";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import { useBdcStatus } from "../../../hooks/vault/queries/useBdcStatus";
import { DynamicFormBuilder } from "./DynamicFormBuilder";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { validToUpload } from "../fixtures/validToUpload";
import {
  isBdcAuthorizationRequirement,
  shouldValidateNativeBdcCompanyData,
} from "../utils/requirementGrouping";
import { removeRequirementProgress } from "../../../utils/vault/requirementProgress";
import { getDefaultFieldDisplayType } from "../../../utils/vault/getDefaultFieldDisplayType";
import { allFormsValidators } from "../../../utils/vault/forms/allFormsValidators";
import { toast } from "sonner";
import { useVaultAutoSave } from "../../../hooks/useVaultAutoSave";
import { RadioField } from "./FormField";
import { CancelToReview } from "../CancelToReview";
import { RentFurnitureModal } from "../modals/RentFurnitureModal";
import { addYears, subDays, format as formatDate, parse as parseDateFns, isValid as isValidDate } from "date-fns";
import { sanitizeDraftFormValues } from "../../../utils/vault/sanitizeDraftFormValues";
import { VaultFormValidationError } from "../../../exceptions/VaultFormValidationError";
import {
  getResolvedOcrFieldKeys,
} from "../../../utils/vault/getResolvedOcrFieldKeys";
import { parseFormDate } from "../../../utils/vault/parseFormDate";
import { canEditValidRequirement } from "../../../utils/auth/requirementEditPermissions";
import { LockedFieldsSummary } from "./LockedFieldsSummary";
import { AlertCircle, Lock } from "lucide-react";
import { BDC_COMPANY_DATA_KEYS } from "../../../api/bdc.api";
import {
  isBdcActorMismatch,
  isNativeBdcSupported,
} from "../../../utils/vault/bdcActor";
import {
  BDC_COMPANY_ADDRESS_LENGTH_ERROR,
  validateBdcCompanyData,
} from "../../../utils/vault/bdcInputValidation";

// Keys that have conditional logic and need to be re-evaluated on form load
const CONDITIONAL_KEYS = [
  "gender",
  "occupation",
  "marital_status",
  "house_type",
  "usage",
  "start_date",
  "includes_furniture",
  "payment_reference_type",
];

// Fields that support "otro" option with an additional text input
const OTHER_OPTION_FIELDS = ["last_landlord_relationship", "occupation"];
const ALWAYS_HIDDEN_ACTOR_FIELDS = new Set(["nationality"]);

// MB-483: which field holds the deposit reference for each selected type. An
// absent/empty type means CLABE, so folios captured before the type existed keep
// rendering exactly as they did.
const PAYMENT_REFERENCE_KEY_BY_TYPE: Record<string, string> = {
  clabe: "clabe",
  account: "account_number",
  card: "card_number",
};
const PAYMENT_REFERENCE_KEYS = Object.values(PAYMENT_REFERENCE_KEY_BY_TYPE);

const resolveReferenceKey = (type?: string | null) =>
  PAYMENT_REFERENCE_KEY_BY_TYPE[String(type ?? "").trim()] ?? "clabe";

// Fields that only make sense when the rent is paid by transfer.
const PAYMENT_FIELD_KEYS = new Set([
  "bank_name",
  "payment_reference_type",
  ...PAYMENT_REFERENCE_KEYS,
  "deposit_beneficiary_name",
]);

type ValidationSummaryItem = {
  key: string;
  label: string;
  message: string;
};

// Type for form field values (form values are always strings in this system)
type FormFieldValue = string | undefined | null;

const BDC_COMPANY_ADDRESS_FIELDS = new Set([
  "street",
  "exterior_number",
  "interior_number",
  "neighborhood",
  "municipality",
  "city",
  "state",
  "postal_code",
]);

const hasFieldValue = (value: FormFieldValue): value is string =>
  value !== undefined && value !== null && String(value).trim() !== "";

function getBdcStrictFieldErrors(
  fieldKey: string,
  values: Record<string, FormFieldValue>,
): Record<string, string> {
  const allErrors = validateBdcCompanyData(
    values as unknown as Record<string, unknown>,
  );
  const errors: Record<string, string> = {};
  if (allErrors[fieldKey]) errors[fieldKey] = allErrors[fieldKey];
  if (BDC_COMPANY_ADDRESS_FIELDS.has(fieldKey) && allErrors.street) {
    errors.street = allErrors.street;
  }
  return errors;
}

interface Props {
  header: React.ReactNode;
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
  /**
   * When true, OCR-verified fields render as editable inputs instead of the
   * read-only LockedFieldsSummary block. Used in the /folders/:id admin view
   * so staff can correct OCR extraction mistakes after "Deshacer envío".
   * Defaults to a pathname-based detection if not provided, so existing
   * call sites (actor /vault/:id) keep the read-only behavior.
   */
  isAdminContext?: boolean;
}

// Interface para los datos del formulario que se guardan en localStorage
interface FormDraftData {
  formValues: Record<string, FormFieldValue>;
  paymentMethod: string;
}

interface ConditionalLogicOptions {
  readonly recalculateEndDate?: boolean;
  /**
   * MB-483: set on the mount/restore passes, which replay the cascade only to
   * establish visibility. Clearing values there would wipe what the operator
   * already captured, so the payment-reference reset runs on explicit user
   * switches only.
   */
  readonly isInitialPass?: boolean;
}

export const GeneralForm: React.FC<Props> = ({
  header,
  onRegisterSubmit,
  isAdminContext,
}) => {
  const dispatch = useAppDispatch();

  const { activeRequirement, activeVault, jwtToken } = useAppSelector(
    (state) => state.vault
  );
  const { selectOptions } = useAppSelector((state) => state.requirements);
  const { activeFolder } = useAppSelector((state) => state.folder);
  // Null-safe: some test stores (and any pre-auth render) omit the auth slice,
  // so reading state.auth and destructuring would crash.
  const userInfo = useAppSelector((state) => state.auth?.userInfo);
  const canEditValidInPlace = canEditValidRequirement(userInfo?.groups);
  const requirementFields = useMemo(
    () => activeRequirement?.form_values ?? [],
    [activeRequirement?.form_values],
  );
  const activeRequirementSubfolder = useMemo(() => {
    if (!activeVault?.subfolders || !activeRequirement) return undefined;
    return (
      activeVault.subfolders.find((subfolder) =>
        subfolder.requirements.some(
          (requirement) => requirement.id === activeRequirement.id,
        ),
      ) ??
      activeVault.subfolders.find(
        (subfolder) =>
          subfolder.slug === activeRequirement.subfolder ||
          subfolder.id === activeRequirement.subfolder,
      )
    );
  }, [activeRequirement, activeVault?.subfolders]);
  const bdcAuthorizationRequirement = useMemo(
    () =>
      activeRequirementSubfolder?.requirements.some(
        (requirement) => requirement.id === activeRequirement?.id,
      )
        ? activeRequirementSubfolder.requirements.find((requirement) =>
            isBdcAuthorizationRequirement(requirement.slug, requirement.type),
          )
        : undefined,
    [activeRequirement?.id, activeRequirementSubfolder],
  );
  const bdcCompanyDataSnapshot =
    bdcAuthorizationRequirement?.verification_data?.bdc?.company_data;
  const confirmedBdcCompanyValues = useMemo(() => {
    if (activeRequirement?.slug !== "business-form") return null;
    if (
      bdcCompanyDataSnapshot?.reviewed !== true ||
      !bdcCompanyDataSnapshot.values ||
      typeof bdcCompanyDataSnapshot.values !== "object" ||
      Array.isArray(bdcCompanyDataSnapshot.values)
    ) {
      return null;
    }

    return Object.fromEntries(
      BDC_COMPANY_DATA_KEYS.map((key) => [
        key,
        String(bdcCompanyDataSnapshot.values[key] ?? "").trim(),
      ]),
    ) as Record<string, string>;
  }, [activeRequirement?.slug, bdcCompanyDataSnapshot]);

  // Full name of the landlord, used to autofill `deposit_beneficiary_name`
  // when the operator marks "same as landlord". Falls back to composing from
  // name + surnames if `full_name` is not set in metadata.
  const landlordFullName = useMemo(() => {
    const l = activeFolder?.metadata?.landlord;
    if (!l) return "";
    if (l.full_name) return l.full_name;
    return [l.name, l.first_surname, l.second_surname]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [activeFolder?.metadata?.landlord]);

  const resolvedOcrFieldKeys = useMemo(
    () => getResolvedOcrFieldKeys(requirementFields),
    [requirementFields],
  );

  // Prop-first with a pathname fallback so legacy mount points (which don't
  // pass the prop) still behave correctly. Same detection style as ID.tsx
  // and BusinessAct.tsx elsewhere in the project.
  const effectiveIsAdminContext = useMemo(() => {
    if (isAdminContext !== undefined) return isAdminContext;
    if (typeof window === "undefined") return false;
    return !window.location.pathname.includes("/vault/");
  }, [isAdminContext]);

  const isBdcCompanyForm =
    activeRequirement?.slug === "business-form" &&
    activeRequirementSubfolder?.slug.toLowerCase().endsWith("-pm") === true;
  const nativeBdcSupported = isNativeBdcSupported(
    bdcAuthorizationRequirement,
    activeRequirementSubfolder,
  );
  const isParticipantMismatch =
    !effectiveIsAdminContext &&
    isBdcActorMismatch(jwtToken, activeRequirementSubfolder?.slug);
  const bdcStatusQuery = useBdcStatus(bdcAuthorizationRequirement?.id, {
    enabled:
      isBdcCompanyForm && nativeBdcSupported && !isParticipantMismatch,
    isAdminContext: effectiveIsAdminContext,
    staleTime: 30_000,
  });
  const strictBdcCompanyValidation =
    isBdcCompanyForm &&
    nativeBdcSupported &&
    shouldValidateNativeBdcCompanyData(
      bdcAuthorizationRequirement,
      bdcStatusQuery.data,
    );

  // Staff (operations/cja/assistant/admin) on the internal /folders/:id view
  // may edit an already-valid requirement in place and save WITHOUT changing
  // its status. Gated: staff group + admin context + status === valid.
  // Non-staff and the actor /vault/:id flow are unaffected.
  const staffEditInPlace =
    effectiveIsAdminContext &&
    canEditValidInPlace &&
    activeRequirement?.status === Status.Valid;
  const isBdcAuthorizationTerminal =
    bdcAuthorizationRequirement?.status === Status.Valid ||
    bdcAuthorizationRequirement?.status === Status.PendingReview;
  const bdcCompanyDataWasConsumed =
    isBdcAuthorizationTerminal ||
    bdcAuthorizationRequirement?.status === Status.Invalid ||
    Boolean(
      bdcAuthorizationRequirement?.verification_data?.bdc
        ?.inputs_consumed_at,
    ) ||
    (Boolean(
      bdcAuthorizationRequirement?.verification_data?.bdc?.last_status,
    ) &&
      bdcAuthorizationRequirement?.verification_data?.bdc?.last_status !==
        "capturing_inputs");
  const enabled =
    validToUpload.includes(activeRequirement?.status) || staffEditInPlace;
  const shouldLockOcrFields =
    !effectiveIsAdminContext &&
    activeRequirement?.status !== Status.Invalid;
  const lockedOcrFieldKeys = useMemo(
    () => (shouldLockOcrFields ? resolvedOcrFieldKeys : new Set<string>()),
    [resolvedOcrFieldKeys, shouldLockOcrFields],
  );
  const lockedBdcCompanyFieldKeys = useMemo(() => {
    if (
      !confirmedBdcCompanyValues ||
      (bdcCompanyDataSnapshot?.source === "business-form" &&
        !bdcCompanyDataWasConsumed)
    ) {
      return new Set<string>();
    }
    const formKeys = new Set(requirementFields.map((field) => field.key));
    return new Set(
      BDC_COMPANY_DATA_KEYS.filter((key) => formKeys.has(key)),
    );
  }, [
    bdcCompanyDataSnapshot?.source,
    bdcCompanyDataWasConsumed,
    confirmedBdcCompanyValues,
    requirementFields,
  ]);
  const lockedFormFieldKeys = useMemo(
    () =>
      new Set([
        ...lockedOcrFieldKeys,
        ...lockedBdcCompanyFieldKeys,
      ]),
    [lockedBdcCompanyFieldKeys, lockedOcrFieldKeys],
  );

  const updateFormMutation = useUpdateVaultFormValues(activeVault?.id);

  // Form state
  const [formValues, setFormValues] = useState<Record<string, FormFieldValue>>({});
  const [requiredFields, setRequiredFields] = useState<Record<string, boolean>>({});
  const [displayFields, setDisplayFields] = useState<Record<string, "block" | "hidden">>({});
  const [labelFields, setLabelFields] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationSummary, setValidationSummary] = useState<ValidationSummaryItem[]>([]);
  // Fields with an async validation in flight (email DNS check on blur can
  // take up to 5s) — used to show a "Verificando…" hint under the input.
  const [validatingFields, setValidatingFields] = useState<Record<string, boolean>>({});
  // Monotonic token per field key: a blur validation that resolves after a
  // newer blur on the same field (or after the value changed) is stale and
  // must not paint its verdict over the newer one's.
  const blurValidationTokenRef = useRef<Record<string, number>>({});

  // Payment method state (special case)
  const [paymentMethod, setPaymentMethod] = useState<string>("cash");

  // UI-only toggle for "is deposit beneficiary the same as landlord?".
  // When true, we copy `landlordFullName` into the real `deposit_beneficiary_name`
  // form field. Not persisted as its own key.
  const [isBeneficiarySameAsLandlord, setIsBeneficiarySameAsLandlord] =
    useState<boolean>(false);

  // Furniture modal state (rent with furniture)
  const [showFurnitureModal, setShowFurnitureModal] = useState(false);
  const hasShownFurnitureModal = useRef(false);

  // "Other" option fields state - tracks which select fields have "otro" selected
  const [otherValueFields, setOtherValueFields] = useState<Record<string, boolean>>({});

  // Ref that always holds the latest formValues so handleBlur never reads stale data
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  // Flag para evitar mostrar toast de recuperación múltiples veces
  const hasShownRecoveryToast = useRef(false);
  // Flag para saber si el formulario ya se inicializó
  const isFormInitialized = useRef(false);
  // In-flight draft restore. The registered submit wrapper awaits this so
  // tapping "Siguiente" right after mount never validates the pre-restore
  // (empty) state — and, because the restore commits via flushSync, the
  // wrapper then dispatches the post-restore handleSubmit closure.
  const pendingRestoreRef = useRef<Promise<void> | null>(null);

  // Refs to break dependency chains that cause spurious re-initialization during polling.
  // In develop, the init useEffect only depended on [activeRequirement?.id].
  // We replicate that pattern using refs so polling doesn't wipe user-entered data.
  const activeRequirementRef = useRef(activeRequirement);
  activeRequirementRef.current = activeRequirement;

  const getFieldLabel = useCallback(
    (field: FormValue): string => labelFields[field.key] ?? field.label ?? field.key,
    [labelFields],
  );

  // MB-483: the reference field the selected type points at. Derived (not state)
  // so it cannot drift from the value actually stored in the form.
  const activeReferenceKey = resolveReferenceKey(
    formValues["payment_reference_type"] as string,
  );

  const isFieldVisibleForUser = useCallback(
    (key: string): boolean => {
      if (displayFields[key] === "hidden") return false;
      // Only the reference matching the selected type is live. The others must be
      // invisible AND unvalidated — shouldValidateField builds on this, so a card
      // number is never checked against the 18-digit CLABE rule.
      if (PAYMENT_REFERENCE_KEYS.includes(key)) {
        return paymentMethod === "transfer" && key === activeReferenceKey;
      }
      if (PAYMENT_FIELD_KEYS.has(key)) return paymentMethod === "transfer";
      return true;
    },
    [activeReferenceKey, displayFields, paymentMethod],
  );

  const shouldValidateField = useCallback(
    (key: string): boolean => {
      if (!isFieldVisibleForUser(key)) return false;
      if (key === "deposit_beneficiary_name" && isBeneficiarySameAsLandlord) {
        return false;
      }
      return true;
    },
    [isBeneficiarySameAsLandlord, isFieldVisibleForUser],
  );

  const scrollToField = useCallback((key: string) => {
    const element =
      document.querySelector(`[data-field-key="${key}"]`) ??
      document.getElementById(key) ??
      document.querySelector(`[id^="${key}-"]`);

    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const applyPaymentMethodState = useCallback(
    (
      method: string,
      fields: FormValue[],
      display: Record<string, "block" | "hidden">,
      required: Record<string, boolean>,
    ) => {
      if (!fields.some((field) => PAYMENT_FIELD_KEYS.has(field.key))) return;

      const isTransfer = method === "transfer";
      // MB-483: the three reference fields share one slot — only the one the
      // selected type points at may show. Without this they would all render at
      // once on load, since they are all payment fields.
      const activeKey = resolveReferenceKey(
        fields.find((field) => field.key === "payment_reference_type")?.value,
      );

      fields.forEach((field) => {
        if (!PAYMENT_FIELD_KEYS.has(field.key)) return;
        const isVisible =
          isTransfer &&
          (!PAYMENT_REFERENCE_KEYS.includes(field.key) || field.key === activeKey);
        display[field.key] = isVisible ? "block" : "hidden";
        required[field.key] = isVisible;
      });
    },
    [],
  );

  // Auto-save hook - vinculado al folderId (folio) + requirementId
  const {
    autoSaveState,
    saveData,
    forceSave,
    clearDraft,
    getStoredData,
    registerSnapshot,
  } = useVaultAutoSave<FormDraftData>(activeVault?.id, activeRequirement?.id, {
    debounceMs: 500,
    // Solo guardar borrador local si el formulario está habilitado. En la
    // edición in-place de admin (requisito válido) se omite el borrador
    // automático y se confía en el guardado explícito "Guardar cambios",
    // evitando toasts de recuperación sobre un requisito ya válido.
    enabled: enabled && !staffEditInPlace,
    serverUpdatedAt: activeRequirement?.updated_at,
  });
  const draftAutoSaveEnabled = enabled && !staffEditInPlace;

  // Resetear flags cuando cambia el requirement
  useEffect(() => {
    hasShownRecoveryToast.current = false;
    isFormInitialized.current = false;
    hasShownFurnitureModal.current = false;
    setOtherValueFields({});
  }, [activeRequirement?.id]);

  // Conditional logic for field visibility
  // Defined before initializeForm so it can be called during initialization
  const handleConditionalLogic = useCallback((key: string, value: FormFieldValue, options: ConditionalLogicOptions = {}) => {
    // Handle gender → marital_status options cascade
    if (key === "gender") {
      dispatch(onSetGenderFields({ form: null, gender: value as string }));
      // Auto-correct marital_status if already has a value
      setFormValues(prev => {
        const currentMaritalStatus = prev["marital_status"] as string;
        if (!currentMaritalStatus) return prev;
        const isSoltero = currentMaritalStatus.toLowerCase().includes("solt");
        if (value === "Masculino") {
          return { ...prev, marital_status: isSoltero ? "Soltero" : "Casado" };
        } else if (value === "Femenino") {
          return { ...prev, marital_status: isSoltero ? "Soltera" : "Casada" };
        }
        return prev;
      });
    }

    // Handle occupation changes (student/unemployed/housework)
    if (key === "occupation") {
      const normalizedValue = value?.toLowerCase() ?? "";
      const isHousework = normalizedValue.includes("hogar");
      const isStudentOrUnemployed =
        normalizedValue.includes("estudiante") ||
        normalizedValue.includes("desempleado") ||
        isHousework;

      const jobFields = [
        "company_website",
        "company_name",
        "job_seniority",
        "company_turn",
        "company_street",
        "company_exterior_number",
        "company_interior_number",
        "company_postal_code",
        "company_neighborhood",
        "company_municipality",
        "job_reference_name",
        "job_reference_phone",
        "job_reference_email",
        "job_reference_relationship",
      ];

      jobFields.forEach((field) => {
        setDisplayFields((prev) => ({
          ...prev,
          [field]: isStudentOrUnemployed ? "hidden" : "block",
        }));
        setRequiredFields((prev) => ({
          ...prev,
          [field]: !isStudentOrUnemployed && !field.includes("interior") && !field.includes("email"),
        }));
      });

      // Clear field values when hiding (matches old behavior)
      if (isStudentOrUnemployed) {
        setFormValues((prev) => {
          const updated = { ...prev };
          jobFields.forEach((field) => { updated[field] = undefined; });
          return updated;
        });
      }
    }

    // Handle marital status changes
    if (key === "marital_status") {
      const isMarried = value?.toLowerCase().includes("casad") ?? false;
      setDisplayFields((prev) => ({
        ...prev,
        marital_regime: isMarried ? "block" : "hidden",
      }));
      setRequiredFields((prev) => ({
        ...prev,
        marital_regime: isMarried,
      }));

      // Clear value when hiding (matches old behavior)
      if (!isMarried) {
        setFormValues((prev) => ({ ...prev, marital_regime: undefined }));
      }
    }

    // Handle housing type changes
    if (key === "house_type") {
      const isRentHouse = value === "Rentada";
      const isFamilyHouse = value === "Vivo con padres o familiares";
      const showLandlordFields = isRentHouse || isFamilyHouse;

      const landlordFields = [
        "last_landlord_name",
        "last_landlord_email",
        "last_landlord_phone",
        "rent_amount",
      ];

      landlordFields.forEach((field) => {
        const originalField = activeRequirementRef.current?.form_values?.find(f => f.key === field);
        const shouldBeRequired = originalField
          ? showLandlordFields && originalField.required
          : showLandlordFields && !field.includes("email"); // fallback

        setDisplayFields((prev) => ({
          ...prev,
          [field]: showLandlordFields ? "block" : "hidden",
        }));
        setRequiredFields((prev) => ({
          ...prev,
          [field]: shouldBeRequired,
        }));
      });

      // last_landlord_relationship is only shown/required for family house, not for rented
      setDisplayFields((prev) => ({
        ...prev,
        last_landlord_relationship: isFamilyHouse ? "block" : "hidden",
      }));
      setRequiredFields((prev) => ({
        ...prev,
        last_landlord_relationship: isFamilyHouse,
      }));

      // Clear field values when hiding (matches old behavior)
      if (!showLandlordFields) {
        setFormValues((prev) => {
          const updated = { ...prev };
          [...landlordFields, "last_landlord_relationship"].forEach((field) => {
            updated[field] = undefined;
          });
          return updated;
        });
      }
      if (!isFamilyHouse) {
        setFormValues((prev) => ({
          ...prev,
          last_landlord_relationship: undefined,
        }));
      }

      // Update labels based on housing type
      if (isRentHouse) {
        setLabelFields((prev) => ({
          ...prev,
          last_landlord_name: "Nombre de tu último arrendador",
          last_landlord_email: "Correo de tu último arrendador",
          last_landlord_phone: "Teléfono de tu último arrendador",
          last_landlord_relationship: "Relación con el último arrendador",
          rent_amount: "¿Cuánto pagas de renta actualmente?",
        }));
      } else if (isFamilyHouse) {
        setLabelFields((prev) => ({
          ...prev,
          last_landlord_name: "Nombre del responsable del domicilio",
          last_landlord_email: "Correo del responsable del domicilio",
          last_landlord_phone: "Teléfono del responsable del domicilio",
          last_landlord_relationship: "Relación con el responsable del domicilio",
          rent_amount: "¿Cuánto aportas al pago de la renta?",
        }));
      }
    }

    // Handle usage type changes (show/hide business_activity)
    if (key === "usage") {
      const showBusinessActivity = value !== "Casa Habitación";
      setDisplayFields((prev) => ({
        ...prev,
        business_activity: showBusinessActivity ? "block" : "hidden",
      }));
      setRequiredFields((prev) => ({
        ...prev,
        business_activity: showBusinessActivity,
      }));

      // Clear value when hiding (matches old behavior)
      if (!showBusinessActivity) {
        setFormValues((prev) => ({ ...prev, business_activity: undefined }));
      }
    }

    // MB-483: show only the reference field matching the selected account type.
    if (key === "payment_reference_type") {
      const activeKey = resolveReferenceKey(value);

      PAYMENT_REFERENCE_KEYS.forEach((refKey) => {
        const isActive = refKey === activeKey;
        setDisplayFields((prev) => ({
          ...prev,
          [refKey]: isActive ? "block" : "hidden",
        }));
        setRequiredFields((prev) => ({ ...prev, [refKey]: isActive }));
      });

      // Only an explicit switch clears the replaced references. Two reasons the
      // clearing matters: a stale number must not reach the contract, and the
      // backend mirrors the active reference into `clabe` for mb_platform — so
      // after a card→CLABE switch the CLABE input would otherwise show the
      // mirrored card digits. The mount pass must NOT run this (see
      // isInitialPass): it would wipe values the operator already captured.
      if (!options.isInitialPass) {
        setFormValues((prev) => {
          const updated = { ...prev };
          PAYMENT_REFERENCE_KEYS.forEach((refKey) => {
            if (refKey !== activeKey) updated[refKey] = undefined;
          });
          // Switching TO CLABE: the backend mirror may have left an account or
          // card number in this key. Those are never 18 digits, so the shape
          // tells a leftover mirror apart from a real CLABE the operator typed
          // before picking the type — which must not be wiped.
          if (
            activeKey === "clabe" &&
            hasFieldValue(prev.clabe) &&
            !/^\d{18}$/.test(String(prev.clabe))
          ) {
            updated.clabe = undefined;
          }
          return updated;
        });
        setValidationSummary((prev) =>
          prev.filter((item) => !PAYMENT_REFERENCE_KEYS.includes(item.key)),
        );
      }
    }

    // Handle start_date changes (auto-calculate end_date = start_date + 1 year - 1 day)
    if (key === "start_date") {
      if (options.recalculateEndDate === false) return;

      if (!value) {
        formValuesRef.current = {
          ...formValuesRef.current,
          end_date: undefined,
        };
        setFormValues((prev) => ({ ...prev, end_date: undefined }));
      } else {
        const parsed = parseDateFns(value, "yyyy-MM-dd", new Date());
        if (isValidDate(parsed)) {
          const oneYearLater = subDays(addYears(parsed, 1), 1);
          const endDateStr = formatDate(oneYearLater, "yyyy-MM-dd");
          formValuesRef.current = {
            ...formValuesRef.current,
            end_date: endDateStr,
          };
          setFormValues((prev) => ({ ...prev, end_date: endDateStr }));
        }
      }
    }

    // Handle includes_furniture changes (show RentFurnitureModal)
    if (key === "includes_furniture") {
      if (value === "Sí" && !hasShownFurnitureModal.current) {
        setShowFurnitureModal(true);
        hasShownFurnitureModal.current = true;
      }
    }

    // Handle "other" option for select fields
    if (OTHER_OPTION_FIELDS.includes(key)) {
      const isOther = value?.toLowerCase().endsWith("otro") ?? false;
      setOtherValueFields((prev) => ({
        ...prev,
        [key]: isOther,
      }));
    }
  }, [dispatch]);

  const initializeForm = useCallback(() => {
    const fields = requirementFields;
    const newValues: Record<string, FormFieldValue> = {};
    const newRequired: Record<string, boolean> = {};
    const newDisplay: Record<string, "block" | "hidden"> = {};
    const newLabels: Record<string, string> = {};
    let nextPaymentMethod = "cash";

    fields.forEach((field) => {
      newValues[field.key] = field.value === "" ? undefined : field.value;
      newRequired[field.key] = field.required;
      // OCR-prefilled fields are displayed normally but rendered as locked (via lockedFields prop).
      newDisplay[field.key] = getDefaultFieldDisplayType(field.key, field.hidden);
      newLabels[field.key] = field.label;

      if (ALWAYS_HIDDEN_ACTOR_FIELDS.has(field.key)) {
        newDisplay[field.key] = "hidden";
      }

      // Check for payment method
      if (PAYMENT_FIELD_KEYS.has(field.key) && hasFieldValue(field.value)) {
        nextPaymentMethod = "transfer";
      }
    });

    // A reviewed BDC snapshot is the canonical value for the fields shared
    // with the broader company form. Keep the latter incomplete until its own
    // submit, but render these values immediately and consistently after reload.
    if (confirmedBdcCompanyValues) {
      Object.entries(confirmedBdcCompanyValues).forEach(([key, value]) => {
        if (key in newValues) newValues[key] = value;
      });
    }

    // MB-483: the method is now a stored field, so a captured value wins over the
    // "any payment field is filled ⇒ transfer" guess above. That guess stays as
    // the fallback for folios captured before the field existed, and for forms
    // the backfill has added it to but nobody has answered yet (value still "").
    const storedPaymentMethod = fields.find((f) => f.key === "payment_method")?.value;
    if (hasFieldValue(storedPaymentMethod)) {
      nextPaymentMethod = storedPaymentMethod;
    }

    applyPaymentMethodState(nextPaymentMethod, fields, newDisplay, newRequired);

    // Seed the resolved method so it is submitted even when the operator never
    // touches the toggle — the common case for a form that only needed one other
    // field fixed. Without this, a folio predating the field (or one the backfill
    // just added it to) keeps posting an empty payment_method, and mb_platform
    // keeps printing "Transferencia Bancaria" while hiding the bank block.
    if (fields.some((field) => field.key === "payment_method")) {
      newValues.payment_method = nextPaymentMethod;
    }

    if ("birth_country" in newValues && "nationality" in newValues) {
      newValues.nationality = newValues.birth_country;
    }

    // Initialize "other" option fields
    const newOtherFields: Record<string, boolean> = {};
    fields.forEach((field) => {
      const val = newValues[field.key];
      if (OTHER_OPTION_FIELDS.includes(field.key) && val?.toLowerCase().endsWith("otro")) {
        newOtherFields[field.key] = true;
      }
    });
    setOtherValueFields(newOtherFields);

    // Hide OCR-locked fields that have values — they are shown in LockedFieldsSummary instead.
    // When a requirement is invalid, users must be able to correct every field.
    if (shouldLockOcrFields) {
      lockedOcrFieldKeys.forEach((key) => {
        const val = newValues[key];
        if (val && String(val).trim() !== "") {
          newDisplay[key] = "hidden";
        }
      });
    }

    // Keep the ref in sync immediately — validateForm/handleSubmit read from
    // it, and the next render (which normally syncs it) may not have happened
    // yet when a submit fires right after initialization.
    formValuesRef.current = newValues;
    setFormValues(newValues);
    setRequiredFields(newRequired);
    setDisplayFields(newDisplay);
    setLabelFields(newLabels);
    setPaymentMethod(nextPaymentMethod);
    setErrors({});
    setValidationSummary([]);
    registerSnapshot({
      formValues: newValues,
      paymentMethod: nextPaymentMethod,
    });

    // Run conditional logic for all fields that have existing values
    // This ensures the form correctly shows/hides fields on load (not just on user interaction)
    Object.entries(newValues).forEach(([key, value]) => {
      if (value && CONDITIONAL_KEYS.includes(key)) {
        handleConditionalLogic(key, value, {
          recalculateEndDate: false,
          isInitialPass: true,
        });
      }
    });

    // Hydrate cascaded dropdowns: country fields fetch their state options
    // lazily on change, so without this re-fire the saved `birth_state`
    // (or any `*_state` paired with a country) has no matching option and
    // the select renders the placeholder until the user re-picks the country.
    Object.entries(newValues).forEach(([key, value]) => {
      if (value && key.includes("country")) {
        dispatch(
          onSetCountryFields({
            form: null,
            country_field: key,
            country_name: value as string,
          }),
        );
      }
    });

    // Same hydration idea for postal codes → neighborhood (colonia) options.
    // We intentionally use `onLoadAddressOptions` (options-only) instead of
    // `onSetAddressFields` here: the latter rewrites neighborhood/state/city
    // with the lookup response and would clobber the user's saved values.
    Object.entries(newValues).forEach(([key, value]) => {
      if (
        value &&
        key.includes("postal_code") &&
        typeof value === "string" &&
        value.length === 5
      ) {
        dispatch(onLoadAddressOptions({ zipCode: value, field: key }));
      }
    });

  }, [
    requirementFields,
    applyPaymentMethodState,
    confirmedBdcCompanyValues,
    handleConditionalLogic,
    registerSnapshot,
    lockedOcrFieldKeys,
    shouldLockOcrFields,
    dispatch,
  ]);

  // Ref to always have the latest initializeForm without adding it as a useEffect dependency.
  // This replicates develop's pattern where the init effect only depended on [activeRequirement?.id].
  const initializeFormRef = useRef(initializeForm);
  initializeFormRef.current = initializeForm;
  const hasActiveFormValues = Boolean(activeRequirement?.form_values);

  // Initialize form when the requirement ID changes (i.e., user navigates to a different requirement).
  // Does NOT re-run when polling refreshes the same requirement — this prevents wiping user-entered data.
  useEffect(() => {
    if (hasActiveFormValues) {
      initializeFormRef.current();
      isFormInitialized.current = true;
    }
  }, [activeRequirement?.id, hasActiveFormValues]);

  // A company confirmation may arrive after this form mounted, while polling
  // the same requirement. Refresh only the canonical, locked BDC fields so the
  // remaining answers and editable business-form source values stay intact.
  useEffect(() => {
    if (!isFormInitialized.current || !confirmedBdcCompanyValues) return;
    if (lockedBdcCompanyFieldKeys.size === 0) return;

    const confirmedValues = Object.fromEntries(
      [...lockedBdcCompanyFieldKeys].map((key) => [
        key,
        confirmedBdcCompanyValues[key],
      ]),
    );
    formValuesRef.current = { ...formValuesRef.current, ...confirmedValues };
    setFormValues((prev) =>
      Object.entries(confirmedValues).every(
        ([key, value]) => prev[key] === value,
      )
        ? prev
        : { ...prev, ...confirmedValues },
    );
  }, [
    activeVault?.id,
    activeRequirement?.id,
    confirmedBdcCompanyValues,
    lockedBdcCompanyFieldKeys,
  ]);

  useEffect(() => {
    if (shouldLockOcrFields) return;

    setDisplayFields((prev) => {
      let changed = false;
      const next = { ...prev };

      requirementFields.forEach((field) => {
        if (!resolvedOcrFieldKeys.has(field.key)) return;
        if (ALWAYS_HIDDEN_ACTOR_FIELDS.has(field.key)) return;

        const defaultDisplay = getDefaultFieldDisplayType(field.key, field.hidden);
        if (next[field.key] === "hidden" && defaultDisplay !== "hidden") {
          next[field.key] = defaultDisplay;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [
    requirementFields,
    resolvedOcrFieldKeys,
    shouldLockOcrFields,
  ]);

  // Handle postal code → address lookup
  const handlePostalCodeChange = useCallback(
    async (key: string, value: FormFieldValue) => {
      if (!key.includes("postal_code")) return;
      const zipCode = value ?? "";
      if (zipCode.length === 5) {
        try {
          const result = await dispatch(
            onSetAddressFields({ form: null, zipCode, field: key })
          ).unwrap();
          // Set address fields from the returned _address data
          if (result?._address) {
            setFormValues((prev) => {
              const updated = { ...prev };
              Object.entries(result._address).forEach(([k, v]) => {
                if (k !== key && v !== undefined) {
                  updated[k] = v as string;
                }
              });
              return updated;
            });
          }
        } catch {
          // API call failed, silently ignore
        }
      } else {
        dispatch(onUnsetAddressFields({ form: null, zipCode, field: key }));
        // Clear address fields
        if (key.includes("company")) {
          setFormValues((prev) => ({
            ...prev,
            company_municipality: undefined,
            company_neighborhood: undefined,
          }));
        } else {
          setFormValues((prev) => ({
            ...prev,
            state: undefined,
            city: undefined,
            municipality: undefined,
            neighborhood: undefined,
          }));
        }
      }
    },
    [dispatch]
  );

  // Handle form value changes
  const handleFieldChange = useCallback((key: string, value: FormFieldValue) => {
    // Update ref immediately so handleBlur (which may fire in the same tick)
    // always reads the latest value instead of stale closure data.
    formValuesRef.current = { ...formValuesRef.current, [key]: value };
    setFormValues((prev) => ({ ...prev, [key]: value }));

    // Clear error when user types
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
    setValidationSummary((prev) => prev.filter((item) => item.key !== key));

    // Handle conditional logic
    handleConditionalLogic(key, value);

    // Handle postal code lookup
    handlePostalCodeChange(key, value);

    // Handle country → state/nationality cascade
    if (key.includes("country")) {
      dispatch(onSetCountryFields({ form: null, country_field: key, country_name: value as string }));
      if (key === "birth_country") {
        setFormValues(prev => ({ ...prev, nationality: value ?? undefined }));
      }
    }
  }, [errors, handleConditionalLogic, handlePostalCodeChange, dispatch]);

  // Handle payment method change
  const handlePaymentMethodChange = (value: string) => {
    setPaymentMethod(value);
    const isTransfer = value === "transfer";
    const activeKey = resolveReferenceKey(
      formValuesRef.current["payment_reference_type"],
    );

    // MB-483: the method is a stored field now, so persist it alongside the
    // local state. Before this it lived only in React state and the draft, which
    // is why mb_platform always received an empty payment_method and the contract
    // claimed "Transferencia Bancaria" while hiding the bank block.
    //
    // Gated on the field existing in THIS form's definition. Folios opened before
    // MB-483 keep the definition they were created with and are not backfilled,
    // and the merge drops submitted keys the stored definition does not know —
    // silently. Writing it anyway would make the operator believe the toggle was
    // saved when the backend threw it away. Without the key we simply keep the
    // pre-MB-483 behaviour: the toggle stays local, exactly as it always was.
    const formKnowsPaymentMethod = (activeRequirementRef.current?.form_values ?? []).some(
      (field) => field.key === "payment_method",
    );
    if (formKnowsPaymentMethod) {
      formValuesRef.current = { ...formValuesRef.current, payment_method: value };
      setFormValues((prev) => ({ ...prev, payment_method: value }));
    }

    setDisplayFields((prev) => ({
      ...prev,
      bank_name: isTransfer ? "block" : "hidden",
      payment_reference_type: isTransfer ? "block" : "hidden",
      deposit_beneficiary_name: isTransfer ? "block" : "hidden",
      ...Object.fromEntries(
        PAYMENT_REFERENCE_KEYS.map((refKey) => [
          refKey,
          isTransfer && refKey === activeKey ? "block" : "hidden",
        ]),
      ),
    }));
    setRequiredFields((prev) => ({
      ...prev,
      bank_name: isTransfer,
      payment_reference_type: isTransfer,
      deposit_beneficiary_name: isTransfer,
      ...Object.fromEntries(
        PAYMENT_REFERENCE_KEYS.map((refKey) => [
          refKey,
          isTransfer && refKey === activeKey,
        ]),
      ),
    }));

    if (!isTransfer) {
      const cleared = {
        bank_name: undefined,
        payment_reference_type: undefined,
        deposit_beneficiary_name: undefined,
        ...Object.fromEntries(PAYMENT_REFERENCE_KEYS.map((refKey) => [refKey, undefined])),
      };
      formValuesRef.current = { ...formValuesRef.current, ...cleared };
      setFormValues((prev) => ({ ...prev, ...cleared }));
      setIsBeneficiarySameAsLandlord(false);
      setValidationSummary((prev) =>
        prev.filter((item) => !PAYMENT_FIELD_KEYS.has(item.key)),
      );
    }
  };

  // Autofill `deposit_beneficiary_name` with the landlord's full name when the
  // operator toggles "same as landlord" on. Only clear on an explicit Sí → No
  // user transition; otherwise reopening a saved transfer form would silently
  // wipe the beneficiary name on mount (the value would equal landlordFullName
  // and isBeneficiarySameAsLandlord defaults to false).
  const prevIsBeneficiarySameAsLandlord = useRef(isBeneficiarySameAsLandlord);
  useEffect(() => {
    const wasSame = prevIsBeneficiarySameAsLandlord.current;
    if (isBeneficiarySameAsLandlord && landlordFullName) {
      handleFieldChange("deposit_beneficiary_name", landlordFullName);
    } else if (wasSame && !isBeneficiarySameAsLandlord) {
      const current = formValuesRef.current.deposit_beneficiary_name;
      if (current === landlordFullName) {
        handleFieldChange("deposit_beneficiary_name", "");
      }
    }
    prevIsBeneficiarySameAsLandlord.current = isBeneficiarySameAsLandlord;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBeneficiarySameAsLandlord, landlordFullName]);

  const sanitizeFormDraftData = useCallback(
    async (
      candidateValues: Record<string, FormFieldValue>,
      options: {
        excludeErroredFields?: boolean;
        respectDisplayState?: boolean;
      } = {}
    ): Promise<FormDraftData> => {
      const fields = requirementFields;
      const sanitizedValues = await sanitizeDraftFormValues(candidateValues, fields, {
        hiddenFieldKeys: lockedFormFieldKeys,
        currentErrors: errors,
        displayFields,
        requiredFields,
        excludeErroredFields: options.excludeErroredFields ?? true,
        respectDisplayState: options.respectDisplayState ?? true,
      });

      return {
        formValues: sanitizedValues,
        paymentMethod,
      };
    },
    [
      requirementFields,
      displayFields,
      errors,
      paymentMethod,
      requiredFields,
      lockedFormFieldKeys,
    ]
  );

  const restoreSavedData = useCallback((savedData: FormDraftData) => {
    // Restore only keys that are editable in this form instance.
    // This prevents stale drafts from overwriting backend-confirmed OCR values.
    const editableFieldKeys = new Set(
      requirementFields
        .map((field) => field.key)
        .filter((key) => !lockedFormFieldKeys.has(key))
    );

    const safeDraftValues = Object.fromEntries(
      Object.entries(savedData.formValues).filter(([key]) => editableFieldKeys.has(key))
    ) as Record<string, FormFieldValue>;

    // Sync the ref immediately so a submit awaiting this restore validates
    // the restored values without depending on a re-render having committed.
    formValuesRef.current = {
      ...formValuesRef.current,
      ...safeDraftValues,
    };
    setFormValues((prev) => ({
      ...prev,
      ...safeDraftValues,
    }));

    if (savedData.paymentMethod) {
      setPaymentMethod(savedData.paymentMethod);

      // MB-483: the draft predates `payment_method` being a stored field, so it
      // carries the toggle outside formValues. Without syncing it here the form
      // would show the restored method while submitting the one seeded from the
      // stored definition — the operator sees "Transferencia" and the contract
      // gets "cash". Gated on the definition knowing the key for the same reason
      // as `handlePaymentMethodChange`: folios opened before MB-483 are not
      // backfilled, and the merge would discard the value without saying so.
      if (editableFieldKeys.has("payment_method")) {
        formValuesRef.current = {
          ...formValuesRef.current,
          payment_method: savedData.paymentMethod,
        };
        setFormValues((prev) => ({
          ...prev,
          payment_method: savedData.paymentMethod,
        }));
      }

      // Re-aplicar lógica condicional del método de pago
      const isTransfer = savedData.paymentMethod === "transfer";
      // The ref already carries the merged draft values, so it is the source of
      // truth for which reference field the restored type points at.
      const activeKey = resolveReferenceKey(
        formValuesRef.current["payment_reference_type"],
      );
      const referenceIsVisible = Object.fromEntries(
        PAYMENT_REFERENCE_KEYS.map((refKey) => [
          refKey,
          isTransfer && refKey === activeKey,
        ]),
      ) as Record<string, boolean>;

      setDisplayFields((prev) => ({
        ...prev,
        bank_name: isTransfer ? "block" : "hidden",
        payment_reference_type: isTransfer ? "block" : "hidden",
        deposit_beneficiary_name: isTransfer ? "block" : "hidden",
        ...Object.fromEntries(
          Object.entries(referenceIsVisible).map(([key, visible]) => [
            key,
            visible ? "block" : "hidden",
          ]),
        ),
      }));
      setRequiredFields((prev) => ({
        ...prev,
        bank_name: isTransfer,
        payment_reference_type: isTransfer,
        deposit_beneficiary_name: isTransfer,
        ...referenceIsVisible,
      }));
    }

    Object.entries(safeDraftValues).forEach(([key, value]) => {
      if (CONDITIONAL_KEYS.includes(key)) {
        handleConditionalLogic(key, value, {
          recalculateEndDate: false,
          isInitialPass: true,
        });
      }
    });
  }, [
    requirementFields,
    handleConditionalLogic,
    lockedFormFieldKeys,
  ]);

  // Restaurar datos guardados automáticamente después de inicializar el formulario
  useEffect(() => {
    // Solo verificar una vez después de que el formulario se inicialice
    if (!isFormInitialized.current || hasShownRecoveryToast.current) return;
    if (!activeVault?.id || !activeRequirement?.id || !draftAutoSaveEnabled) return;

    let cancelled = false;

    const restoreDraft = async () => {
      const storedData = getStoredData();

      if (!storedData || !storedData.formValues || Object.keys(storedData.formValues).length === 0) {
        return;
      }

      const sanitizedStoredData = await sanitizeFormDraftData(storedData.formValues, {
        excludeErroredFields: false,
        respectDisplayState: false,
      });

      if (cancelled) return;

      // sanitizeFormDraftData echoes the component's CURRENT paymentMethod
      // (not yet restored); keep the draft's so a saved "transfer" form
      // restores its payment fields as visible/required again.
      const restoredData: FormDraftData = {
        ...sanitizedStoredData,
        paymentMethod:
          storedData.paymentMethod || sanitizedStoredData.paymentMethod,
      };

      hasShownRecoveryToast.current = true;
      // Commit the restored state synchronously. The submit wrapper awaits
      // this restore and then calls the freshly re-registered handleSubmit;
      // without flushSync that closure would still see the pre-restore
      // paymentMethod/displayFields/requiredFields and could skip validating
      // fields the draft just made visible/required (e.g. bank_name/clabe
      // when the restored payment method is "transfer").
      flushSync(() => {
        restoreSavedData(restoredData);
      });

      const draftChanged =
        JSON.stringify(storedData.formValues) !== JSON.stringify(restoredData.formValues);

      if (draftChanged) {
        forceSave(restoredData);
      } else {
        registerSnapshot(restoredData, {
          source: "draft",
          lastSaved: autoSaveState.lastSaved,
        });
      }
    };

    // Expose the in-flight restore so the registered submit wrapper can await
    // it instead of validating the pre-restore state (the restore is async:
    // it reads localStorage and runs sanitize validators before applying).
    const restorePromise = restoreDraft().finally(() => {
      if (pendingRestoreRef.current === restorePromise) {
        pendingRestoreRef.current = null;
      }
    });
    pendingRestoreRef.current = restorePromise;

    return () => {
      cancelled = true;
    };
  }, [
    activeVault?.id,
    activeRequirement?.id,
    draftAutoSaveEnabled,
    getStoredData,
    restoreSavedData,
    registerSnapshot,
    autoSaveState.lastSaved,
    sanitizeFormDraftData,
    forceSave,
  ]);

  // Auto-guardar cuando cambian los valores del formulario (con debounce)
  useEffect(() => {
    // No guardar si el formulario no está inicializado o si estamos restaurando
    if (!isFormInitialized.current || !draftAutoSaveEnabled) return;
    if (Object.keys(formValues).length === 0) return;

    let cancelled = false;

    const persistDraft = async () => {
      const sanitizedDraftData = await sanitizeFormDraftData(formValues);
      if (cancelled) return;
      saveData(sanitizedDraftData);
    };

    void persistDraft();

    return () => {
      cancelled = true;
    };
  }, [formValues, errors, draftAutoSaveEnabled, saveData, sanitizeFormDraftData]);

  // Validate a single field on blur — provides real-time feedback.
  // Reads from formValuesRef so it always sees the latest value, even when
  // called synchronously right after an onChange that queued a state update.
  const handleBlur = useCallback(async (key: string) => {
    const field = requirementFields.find((f) => f.key === key);
    if (!field) return;

    const isRequired = requiredFields[key];
    const shouldValidate = shouldValidateField(key);
    const value = formValuesRef.current[key];

    if (!shouldValidate) return;

    // Required check
    if (isRequired && (value === undefined || value === "" || value === null)) {
      setErrors((prev) => ({ ...prev, [key]: "Campo requerido" }));
      return;
    }

    if (strictBdcCompanyValidation) {
      const strictErrors = getBdcStrictFieldErrors(
        key,
        formValuesRef.current,
      );
      setErrors((prev) => {
        const next = { ...prev };
        if (BDC_COMPANY_ADDRESS_FIELDS.has(key)) {
          if (strictErrors.street) next.street = strictErrors.street;
          else if (next.street === BDC_COMPANY_ADDRESS_LENGTH_ERROR) {
            delete next.street;
          }
        }
        if (strictErrors[key]) next[key] = strictErrors[key];
        return next;
      });
      if (strictErrors[key]) return;
    }

    // Textarea length validations
    if (field.type === "textarea" && value) {
      if (isRequired && String(value).length < 10) {
        setErrors((prev) => ({ ...prev, [key]: "Debe tener al menos 10 caracteres" }));
        return;
      }
      if (String(value).length > 500) {
        setErrors((prev) => ({ ...prev, [key]: "No debe exceder 500 caracteres" }));
        return;
      }
    }

    // Field-specific validator
    const fieldValidator = allFormsValidators[key];
    if (fieldValidator && value) {
      // Email validators hit the DNS-lookup service (up to 5s) — surface a
      // pending hint so the late error doesn't appear "out of nowhere".
      const isAsyncEmailCheck =
        field.type === "email" || key === "email" || key.endsWith("_email") || /^email_\d+$/.test(key);
      const token = (blurValidationTokenRef.current[key] ?? 0) + 1;
      blurValidationTokenRef.current[key] = token;
      // Stale = a newer blur superseded this one, or the value changed
      // while the validator awaited (its verdict is about old input).
      const isStale = () =>
        blurValidationTokenRef.current[key] !== token ||
        formValuesRef.current[key] !== value;
      if (isAsyncEmailCheck) {
        setValidatingFields((prev) => ({ ...prev, [key]: true }));
      }
      try {
        await fieldValidator(null, value);
        if (isStale()) return;
        // Validation passed — clear error
        setErrors((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } catch (err: any) {
        if (isStale()) return;
        setErrors((prev) => ({ ...prev, [key]: err?.message || "Campo inválido" }));
        return;
      } finally {
        // Only the latest blur owns the hint — an older lookup resolving
        // late must not hide the spinner of one still in flight.
        if (isAsyncEmailCheck && blurValidationTokenRef.current[key] === token) {
          setValidatingFields((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
        }
      }
    } else {
      // No validator or empty value (and not required) — clear error
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [
    requirementFields,
    requiredFields,
    shouldValidateField,
    strictBdcCompanyValidation,
  ]);

  // Validate form using field-specific validators from allFormsValidators
  // Returns the errors + summary so callers can use them directly (avoids stale closure)
  const validateForm = async (): Promise<{
    errors: Record<string, string>;
    summary: ValidationSummaryItem[];
  }> => {
    const newErrors: Record<string, string> = {};
    const nextValidationSummary: ValidationSummaryItem[] = [];
    const fields = requirementFields;

    for (const field of fields) {
      const isRequired = requiredFields[field.key];
      const shouldValidate = shouldValidateField(field.key);
      // Read from the ref (like handleBlur) so a submit that awaited the
      // draft restore validates the restored values, not a stale closure.
      const value = formValuesRef.current[field.key];

      if (!shouldValidate) continue;

      // Required check
      if (isRequired && (value === undefined || value === "" || value === null)) {
        newErrors[field.key] = "Campo requerido";
        nextValidationSummary.push({
          key: field.key,
          label: getFieldLabel(field),
          message: "Campo requerido",
        });
        continue;
      }

      // Textarea length validations
      if (field.type === "textarea" && value) {
        if (isRequired && value.length < 10) {
          newErrors[field.key] = "Debe tener al menos 10 caracteres";
          nextValidationSummary.push({
            key: field.key,
            label: getFieldLabel(field),
            message: "Debe tener al menos 10 caracteres",
          });
          continue;
        }
        if (value.length > 500) {
          newErrors[field.key] = "No debe exceder 500 caracteres";
          nextValidationSummary.push({
            key: field.key,
            label: getFieldLabel(field),
            message: "No debe exceder 500 caracteres",
          });
          continue;
        }
      }

      // Field-specific validator from allFormsValidators
      const fieldValidator = allFormsValidators[field.key];
      if (fieldValidator && value) {
        try {
          await fieldValidator(null, value);
        } catch (err: any) {
          newErrors[field.key] = err?.message || "Campo inválido";
          nextValidationSummary.push({
            key: field.key,
            label: getFieldLabel(field),
            message: err?.message || "Campo inválido",
          });
        }
      }
    }

    if (strictBdcCompanyValidation) {
      const strictErrors = validateBdcCompanyData(
        formValuesRef.current as unknown as Record<string, unknown>,
      );
      for (const [key, message] of Object.entries(strictErrors)) {
        newErrors[key] = message;
        for (let index = nextValidationSummary.length - 1; index >= 0; index--) {
          if (nextValidationSummary[index].key === key) {
            nextValidationSummary.splice(index, 1);
          }
        }
        const field = fields.find((candidate) => candidate.key === key);
        nextValidationSummary.push({
          key,
          label: field ? getFieldLabel(field) : key,
          message,
        });
      }
    }

    // Cross-field: end_date must not precede start_date (mirrors the
    // backend RV001 rule so the error appears pre-submit).
    if (!newErrors["start_date"] && !newErrors["end_date"]) {
      const endField = fields.find((f) => f.key === "end_date");
      const startRaw = formValuesRef.current["start_date"];
      const endRaw = formValuesRef.current["end_date"];
      const start = typeof startRaw === "string" ? parseFormDate(startRaw) : undefined;
      const end = typeof endRaw === "string" ? parseFormDate(endRaw) : undefined;
      if (endField && shouldValidateField("end_date") && start && end && end < start) {
        const message = "La fecha de término debe ser posterior a la fecha de inicio";
        newErrors["end_date"] = message;
        nextValidationSummary.push({
          key: "end_date",
          label: getFieldLabel(endField),
          message,
        });
      }
    }

    setErrors(newErrors);
    setValidationSummary(nextValidationSummary);
    return { errors: newErrors, summary: nextValidationSummary };
  };

  // Submit form - returns true on success, false on failure
  const handleSubmit = async (): Promise<boolean> => {
    if (!activeRequirement?.form_values || !activeVault) return false;

    const { errors: validationErrors, summary } = await validateForm();
    if (summary.length > 0) {
      // Name the fields WITH their reasons — a label-only list reads as
      // "this field is wrong" even when the real cause is e.g. the email
      // domain check, which users (and support) couldn't diagnose.
      const shown = summary
        .slice(0, 3)
        .map((item) => `${item.label}: ${item.message}`);
      const remaining = summary.length - shown.length;
      toast.error("No pudimos enviar el formulario", {
        description:
          shown.join(" · ") +
          (remaining > 0
            ? ` · y ${remaining} campo${remaining > 1 ? "s" : ""} más`
            : ""),
      });
      // Field keys and rule messages only — never field values (PII).
      Sentry.addBreadcrumb({
        category: "vault.form",
        message: "Submit blocked by client-side validation",
        level: "warning",
        data: {
          requirementId: activeRequirement.id,
          failedFields: summary.map((item) => ({
            key: item.key,
            message: item.message,
          })),
        },
      });
      // Scroll to first error using the returned errors (avoids stale state)
      const firstErrorKey = Object.keys(validationErrors)[0];
      scrollToField(firstErrorKey);
      return false;
    }

    const newFormValues: FormValue[] = activeRequirement.form_values.map(
      (formItem) => {
        const isVisibleForUser = isFieldVisibleForUser(formItem.key);
        const isSameLandlordBeneficiary =
          formItem.key === "deposit_beneficiary_name" &&
          paymentMethod === "transfer" &&
          isBeneficiarySameAsLandlord;
        const currentValue = formValuesRef.current[formItem.key];
        const value = isSameLandlordBeneficiary
          ? landlordFullName
          : isVisibleForUser
            ? (currentValue ?? "")
            : hasFieldValue(currentValue)
              ? currentValue
              : "";
        const shouldKeepHiddenRequirement =
          !isVisibleForUser &&
          hasFieldValue(currentValue) &&
          (lockedOcrFieldKeys.has(formItem.key) ||
            ALWAYS_HIDDEN_ACTOR_FIELDS.has(formItem.key));

        return {
          ...formItem,
          value,
          required: isVisibleForUser
            ? (requiredFields[formItem.key] ?? formItem.required)
            : shouldKeepHiddenRequirement
              ? (requiredFields[formItem.key] ?? formItem.required)
              : false,
          label: labelFields[formItem.key] ?? formItem.label,
        };
      }
    );

    try {
      // Admin in-place edit of a valid requirement: persist ONLY form_values
      // and omit `status` entirely so the backend keeps the requirement valid
      // and does not fire status-transition signals.
      await updateFormMutation.mutateAsync({
        reqId: activeRequirement.id,
        form_values: newFormValues,
        ...(staffEditInPlace
          ? {}
          : { status: Status.PendingReview }),
      });
      // mutation.onSuccess handles: invalidateQueries + dispatch(setActiveRequirement)
      dispatch(setActivePanels([]));

      removeRequirementProgress(activeVault.id, activeRequirement.id);
      // Limpiar el borrador de localStorage después de envío exitoso
      clearDraft();
      toast.success(
        staffEditInPlace
          ? "Cambios guardados correctamente"
          : "Formulario enviado correctamente",
      );
      return true;
    } catch (error: unknown) {
      if (error instanceof VaultFormValidationError) {
        // Backend field-level rejection (RV001): render it exactly like
        // client-side validation — mark the fields, list the reasons,
        // scroll to the first one.
        const fields = activeRequirement.form_values;
        const backendSummary: ValidationSummaryItem[] = Object.entries(
          error.fields,
        ).map(([key, message]) => {
          const field = fields.find((f) => f.key === key);
          return {
            key,
            label: field ? getFieldLabel(field) : key,
            message,
          };
        });
        setErrors((prev) => ({ ...prev, ...error.fields }));
        setValidationSummary(backendSummary);
        toast.error(error.message, {
          description: backendSummary
            .slice(0, 3)
            .map((item) => `${item.label}: ${item.message}`)
            .join(" · "),
        });
        if (backendSummary[0]) scrollToField(backendSummary[0].key);
        return false;
      }
      const message = error instanceof Error ? error.message : "Error al enviar el formulario";
      toast.error(message);
      return false;
    }
  };

  // Register submit function with parent for external triggering (e.g., from "Siguiente" button)
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => {
      // If a draft restore is in flight (e.g. the user tapped "Siguiente"
      // right after a reload), wait for it BEFORE reading submitFnRef: the
      // restore commits via flushSync, which re-registers handleSubmit with
      // closures over the restored values, visibility and required flags.
      // Loop because a re-render can cancel an in-flight restore and replace
      // it with a fresh one; awaiting only the first would proceed without
      // the restored state.
      while (pendingRestoreRef.current) {
        await pendingRestoreRef.current;
      }
      return submitFnRef.current?.() ?? false;
    };
    onRegisterSubmit(fn);
    // Parent owns the reset (e.g. FormView.resetModalSubmit on modal close
    // / requirement change). Removing cleanup here prevents the submit
    // handler from being de-registered on intermediate re-renders, which
    // was hiding the "Enviar a revisión" button after "Deshacer".
  }, [onRegisterSubmit]);

  // Check if bank_name field exists (for payment method toggle)
  const hasBankField = requirementFields.some(
    (f) => f.key === "bank_name"
  );

  // Compute form items to render — OCR locked fields with values are hidden
  // and their separators are transferred to the next visible field
  const visibleFormItems = useMemo(() => {
    const items = requirementFields;
    const result: FormValue[] = [];
    let pendingSeparator: string | undefined;

    for (const item of items) {
      // OCR fields render as editable inputs when the requirement is invalid.
      const isHiddenOcr =
        shouldLockOcrFields &&
        lockedOcrFieldKeys.has(item.key) &&
        formValues[item.key] &&
        String(formValues[item.key]).trim() !== "";

      if (item.separator) {
        pendingSeparator = item.separator;
      }

      if (isHiddenOcr) continue;

      if (pendingSeparator) {
        result.push({ ...item, separator: pendingSeparator });
        pendingSeparator = undefined;
      } else {
        result.push(item);
      }
    }

    return result;
  }, [
    requirementFields,
    lockedOcrFieldKeys,
    formValues,
    shouldLockOcrFields,
  ]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <p className="text-gray-600">{header}</p>
        {lockedBdcCompanyFieldKeys.size > 0 && (
          <p className="mt-2 flex items-start gap-2 text-pretty text-sm text-slate-500">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Los campos con candado ya fueron confirmados para Buró de Crédito.
            </span>
          </p>
        )}
      </div>

      {/* Invalid Requirement Reason */}
      {activeRequirement && <InvalidRequirementReason requirement={activeRequirement} />}

      {validationSummary.length > 0 && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="min-w-0">
              <p className="font-semibold">Revisa estos campos para continuar</p>
              <ul className="mt-2 space-y-1">
                {validationSummary.map((item) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => scrollToField(item.key)}
                      className="text-left font-medium underline decoration-red-300 underline-offset-2 hover:text-red-700"
                    >
                      {item.label}
                    </button>
                    <span className="font-normal">: {item.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Verified OCR data summary (locked fields shown as read-only data).
          Hidden in admin context so staff can edit those fields directly. */}
      {shouldLockOcrFields && (
        <LockedFieldsSummary
          formValues={requirementFields}
          resolvedOcrFieldKeys={lockedOcrFieldKeys}
        />
      )}

      {/* Form Fields */}
      <div className="flex flex-wrap -mx-2 items-end">
        {(() => {
          const beneficiaryItem = visibleFormItems.find(
            (i) => i.key === "deposit_beneficiary_name",
          );
          return visibleFormItems.map((formItem) => {
            // Render deposit_beneficiary_name inline inside the bank_name block
            // (right after "Método de pago" + "¿mismo arrendador?"), so skip it
            // when the map hits it naturally later.
            if (formItem.key === "deposit_beneficiary_name") {
              return null;
            }

            // MB-483: `payment_method` became a stored field, but it is still
            // rendered by the hand-built RadioField in the bank_name block below
            // (which owns the layout and the cash/transfer cascade). Skip the
            // definition-driven copy so the toggle does not render twice.
            if (formItem.key === "payment_method") {
              return null;
            }

            // Render payment method toggle before bank_name
            if (formItem.key === "bank_name" && hasBankField) {
              const isTransfer = paymentMethod === "transfer";
              return (
                <React.Fragment key={`payment_${formItem.order}`}>
                  <div className="w-full md:w-1/2 md:px-2 mb-5">
                    <RadioField
                      name="payment_method"
                      label="Método de pago"
                      value={paymentMethod}
                      onChange={handlePaymentMethodChange}
                      options={[
                        { label: "Efectivo", value: "cash" },
                        { label: "Transferencia", value: "transfer" },
                      ]}
                      required
                      disabled={!enabled}
                    />
                  </div>
                  {isTransfer && beneficiaryItem && (
                    <>
                      <div className="w-full md:w-1/2 md:px-2 mb-5">
                        <RadioField
                          name="is_beneficiary_same_as_landlord"
                          label="¿El beneficiario del depósito es el arrendador?"
                          value={isBeneficiarySameAsLandlord ? "Sí" : "No"}
                          onChange={(v) => {
                            setIsBeneficiarySameAsLandlord(v === "Sí");
                            setValidationSummary((prev) =>
                              prev.filter(
                                (item) => item.key !== "deposit_beneficiary_name",
                              ),
                            );
                          }}
                          options={[
                            { label: "Sí", value: "Sí" },
                            { label: "No", value: "No" },
                          ]}
                          required
                          disabled={!enabled || !landlordFullName}
                        />
                        {!landlordFullName && (
                          <div className="text-xs text-slate-500 mt-1">
                            (No hay datos del arrendador para autocompletar)
                          </div>
                        )}
                      </div>
                      {!isBeneficiarySameAsLandlord && (
                        <DynamicFormBuilder
                          key={`beneficiary_${beneficiaryItem.order}`}
                          formItem={beneficiaryItem}
                          value={formValues[beneficiaryItem.key]}
                          onChange={handleFieldChange}
                          requiredFields={requiredFields}
                          displayFields={displayFields}
                          labelFields={labelFields}
                          disabled={!enabled}
                          errors={errors}
                          lockedFields={[...lockedFormFieldKeys]}
                          dynamicOptions={selectOptions}
                          otherValueFields={otherValueFields}
                        />
                      )}
                    </>
                  )}
                  <DynamicFormBuilder
                    key={formItem.order}
                    formItem={formItem}
                    value={formValues[formItem.key]}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    requiredFields={requiredFields}
                    displayFields={displayFields}
                    labelFields={labelFields}
                    disabled={!enabled}
                    errors={errors}
                    validatingFields={validatingFields}
                    lockedFields={[...lockedFormFieldKeys]}
                    dynamicOptions={selectOptions}
                    otherValueFields={otherValueFields}
                  />
                </React.Fragment>
              );
            }

            return (
              <DynamicFormBuilder
                key={formItem.order}
                formItem={formItem}
                value={formValues[formItem.key]}
                onChange={handleFieldChange}
                onBlur={handleBlur}
                requiredFields={requiredFields}
                displayFields={displayFields}
                labelFields={labelFields}
                disabled={!enabled}
                errors={errors}
                validatingFields={validatingFields}
                lockedFields={[...lockedFormFieldKeys]}
                dynamicOptions={selectOptions}
                otherValueFields={otherValueFields}
              />
            );
          });
        })()}
      </div>

      {/* Rent Furniture Modal */}
      <RentFurnitureModal
        open={showFurnitureModal}
        onClose={() => setShowFurnitureModal(false)}
      />

      {/* Cancel to review (when already submitted) */}
      {!enabled &&
        activeRequirement?.status === Status.PendingReview &&
        activeRequirement && (
          <div className="flex justify-center pt-6 border-t border-gray-100 mt-6">
            <CancelToReview requirement={activeRequirement} />
          </div>
        )}
    </div>
  );
};

export default GeneralForm;
